import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { forkJoin, Observable, of } from 'rxjs';
import { ciInterface, ciInterface2 } from 'src/app/util/interfaces/ci';
import { AuthLoginService } from 'src/app/page/auth-page/service/auth-login.service';
import { environment } from 'src/environments/environment';
import { map, switchMap, tap } from 'rxjs/operators';
import { TreeNode } from 'primeng/api';
import { genericFormInterface } from 'src/app/util/interfaces/genericForm';
import { ProfileService } from 'src/app/services/profile/profile.service';
import { logInterface } from 'src/app/util/interfaces/log.interface';
import { LabelsService } from 'src/app/util/services/labels/labels.service';
import { attachedFilesInterface } from 'src/app/util/interfaces/attachedFiles';
import { mspFileInterface } from 'src/app/util/interfaces/mspFile';
import { ShkDate } from 'shk-date';
import { CmdbDigitalTerm } from 'src/app/util/interfaces/cmdbDigitalTerm';

@Injectable({
	providedIn: 'root',
})
export class CmdbInfoPageService {
	private getCiMinDataUrl = environment.baseApiUrl + 'cmdbrp/{idCi}';
	private getCiBaselineUrl = environment.baseApiUrl + 'cmdb/baseline/{idCi}?{queryString}';
	private getCiTypeFormUrl = environment.baseApiUrl + 'cmdb/citype/{idCiType}?customer_id={idCustomer}';
	private getCiLogsUrl = environment.baseApiUrl + 'cmdb/logs/{idCi}';
	private getCiChildrenUrl = environment.baseApiUrl + 'cmdb/relations/{idCi}';
	private getCmdbPermissionsUrl = environment.baseApiUrl + 'cmdb/permission/{idCiType}';
	private getFileUrl = environment.baseApiUrl + 'cmdb/{idCi}/file?file_id={idFile}';
	private getFileListUrl = environment.baseApiUrl + 'cmdb/{idCi}/file';
	private getCiStatusLog = environment.baseApiUrl + 'cmdb/status/log/{idCi}';
	private getExistingWorkstationOnAd = environment.baseApiUrl + 'cmdb/checkItemAd';
	private getCiStockData = environment.baseApiUrl + 'stock/ciLendingInformation/{idCi}';

	private getLogDigitalTermUrl = environment.baseApiUrl + 'cmdbDigitalTerm/logDigitalTerm/{idCMDB}';

	private getTermFileUrl = environment.baseApiUrl + 'cmdbDigitalTerm/file/{fileName}';

	private headers = new HttpHeaders({
		Authorization: 'Bearer ' + this.AuthLoginService.getSessionToken(),
		'Content-Type': 'application/json;charset=UTF-8',
	});

	constructor(
		private http: HttpClient,
		private AuthLoginService: AuthLoginService,
		private ProfileService: ProfileService,
		private LabelsService: LabelsService
	) {}

	public existingInAd = false;
	public lastGetImpactMap: TreeNode[] = [];
	public lastGetImpactMapNode: TreeNode;

	getImpactMapNodeAsCi(idNode: string): Observable<TreeNode> {
		const today = this.formatDateToService(new Date());
		const queryString = `date=${today}`;
		let getCiBaselineUrl = this.getCiBaselineUrl.replace('{idCi}', idNode).replace('{queryString}', queryString);
		getCiBaselineUrl += `&isAnalystView=${localStorage.getItem('sessionStatus') === '1'}`;

		const getCiChildrenUrl = this.getCiChildrenUrl.replace('{idCi}', idNode);
		const getCiStockData = this.getCiStockData.replace('{idCi}', idNode);

		return this.http.get(getCiChildrenUrl, { headers: this.headers }).pipe(
			switchMap((ciRelationRes: any) => {
				return this.http.get<any>(getCiBaselineUrl, { headers: this.headers }).pipe(
					switchMap((ciRes) => {
						return this.http.get<any>(getCiStockData, { headers: this.headers }).pipe(
							map((stockData) => {
								const newImpactMapData: TreeNode = {
									label: ciRes.identification,
									expanded: true,
									data: {
										isLeaf: ciRelationRes.childs.length > 0, //É folha se não tem filhos, mas nesse caso a gente expande de um em um, então no momento ele é folha
										isRoot: ciRelationRes.parents.length > 0, //É raiz se não tem pai, mas nesse caso a gente expande de um em um, então no momento ele é raiz
										ciType: ciRes.type,
										ciArray: [{}],
									},
									children: [],
									parent: null,
								};

								if (ciRelationRes.parents.length > 0) {
									const newParent: TreeNode['parent'] = {
										label: ciRelationRes.parents[0].name,
										data: {
											id: ciRelationRes.parents[0].id_related,
										},
									};
									newImpactMapData.parent = newParent;
								}

								if (ciRes.cmdb) {
									for (const cmdbItem of Object.keys(ciRes.cmdb)) {
										newImpactMapData.data.ciArray[0][cmdbItem] = ciRes.cmdb[cmdbItem];
									}
								}

								if (ciRes.ci) {
									for (const ciItem of Object.keys(ciRes.ci)) {
										newImpactMapData.data.ciArray[0][ciItem] = ciRes.ci[ciItem];
									}
								}

								if (ciRes.customFields) {
									for (const ciCustomItem of ciRes.customFields) {
										newImpactMapData.data.ciArray[0][ciCustomItem.field] = ciCustomItem.content;
									}
								}

								newImpactMapData.data.ciArray[0]['stock'] = {};
								newImpactMapData.data.ciArray[0]['stock']['responsible'] = stockData.fullName;
								newImpactMapData.data.ciArray[0]['stock']['isConsumable'] = stockData.isConsumable;
								newImpactMapData.data.ciArray[0]['stock']['quantityStock'] = stockData.quantityStock;
								newImpactMapData.data.ciArray[0]['stock']['quantityUse'] = stockData.quantityUse;

								let newDate = new ShkDate();
								if (stockData.expectedReturnDate) {
									newDate = new ShkDate(stockData.expectedReturnDate);
									newDate.setDateFormat('DMY');
									newDate.setDateSeparator('/');
									newImpactMapData.data.ciArray[0]['stock']['expectedReturnDate'] = newDate.getDate();
								} else {
									newImpactMapData.data.ciArray[0]['stock']['expectedReturnDate'] = 'N/A';
								}

								if (stockData.returnDate) {
									newDate = new ShkDate(stockData.returnDate);
									newDate.setDateFormat('DMY');
									newDate.setDateSeparator('/');
									newImpactMapData.data.ciArray[0]['stock']['returnDate'] = newDate.getDate();
								} else {
									newImpactMapData.data.ciArray[0]['stock']['returnDate'] = 'N/A';
								}
								return newImpactMapData;
							})
						);
					})
				);
			})
		);
	}

	getImpactMapDefaultCi(): Observable<TreeNode> {
		return of(this.lastGetImpactMapNode);
	}

	setImpactMapDefaultCi(ci: TreeNode<any>): void {
		this.lastGetImpactMapNode = ci;
	}

	getCiChildren(idFatherCi: string): Observable<Observable<TreeNode<any>[]>> {
		const getCiChildrenUrl = this.getCiChildrenUrl.replace('{idCi}', idFatherCi);

		return this.http.get(getCiChildrenUrl, { headers: this.headers }).pipe(
			map((ciRelationRes: any) => {
				const childArray: Observable<TreeNode>[] = [];
				ciRelationRes.childs.forEach((childData: any) => {
					const temporaryRequest = this.getImpactMapNodeAsCi(childData.id_related);
					childArray.push(temporaryRequest);
				});

				return forkJoin(childArray);
			})
		);
	}

	getCiParent(idChildrenCi: string): Observable<Observable<TreeNode<any>[]>> {
		const getCiParentUrl = this.getCiChildrenUrl.replace('{idCi}', idChildrenCi);

		return this.http.get(getCiParentUrl, { headers: this.headers }).pipe(
			map((ciRelationRes: any) => {
				const parentArray: Observable<TreeNode>[] = [];
				ciRelationRes.parents.forEach((parentData: any) => {
					const temporaryRequest = this.getImpactMapNodeAsCi(parentData.id_related);
					parentArray.push(temporaryRequest);
				});

				return forkJoin(parentArray);
			})
		);
	}

	getCiRelationship(idCi: string): Observable<TreeNode<any>> {
		const getCiRelationsUrl = this.getCiChildrenUrl.replace('{idCi}', idCi);

		return this.http.get(getCiRelationsUrl, { headers: this.headers }).pipe(
			map((ciRelationRes: any) => {
				const relationshipNode: TreeNode = {
					children: [],
					parent: null,
				};

				relationshipNode.parent = {
					data: ciRelationRes.parents[0],
				};
				ciRelationRes.childs.forEach((childData: any) => {
					relationshipNode.children.push({ data: childData });
				});

				return relationshipNode;
			})
		);
	}

	getRegisterForm(idType: string): Observable<genericFormInterface[]> {
		const defaultCustomer = this.ProfileService.userData().idCustomer;
		let getCiTypeFormUrl = this.getCiTypeFormUrl.replace('{idCiType}', idType).replace('{idCustomer}', defaultCustomer);
		getCiTypeFormUrl += `&isAnalystView=${localStorage.getItem('sessionStatus') === '1'}`;

		return this.http.get<JSON[]>(getCiTypeFormUrl, { headers: this.headers }).pipe(
			map((ciFormRes: any) => {
				const newFormArray: genericFormInterface[] = [];

				ciFormRes.cmdb.forEach((formItem: any) => {
					const newForm: genericFormInterface = {
						id: formItem.id,
						disabled: false,
						items: [],
						label: formItem.label,
						name: formItem.name,
						required: false,
						hidden: formItem.hidden,
						values: formItem.values !== null && formItem.values.length > 0 ? formItem.values : [''],
						inputType: formItem.inputType,
						subSelect: [],
					};

					if (formItem.subSelect)
						for (const subSelect of formItem.subSelect) {
							newForm.subSelect.push({
								subSelectQryName: subSelect.subSelectQryName,
								subSelectChildName: subSelect.subSelectChildName,
								parameters: subSelect.field_as_parameters,
							});
						}

					const newFormItemArray: genericFormInterface[] = [];
					for (const item of formItem.items) {
						const newFormItem: genericFormInterface = {
							values: [item.value],
							name: item.name,
							label: item.label,
						};

						newFormItemArray.push(newFormItem);
					}

					newForm.items = newFormItemArray;

					newFormArray.push(newForm);
				});

				ciFormRes.ci.forEach((formItem: any) => {
					const newForm: genericFormInterface = {
						id: formItem.id,
						disabled: false,
						items: [],
						label: formItem.label,
						name: formItem.name,
						required: false,
						hidden: formItem.hidden,
						values: formItem.values !== null && formItem.values.length > 0 ? formItem.values : [''],
						inputType: formItem.inputType,
						subSelect: [],
					};

					if (formItem.subSelect)
						for (const subSelect of formItem.subSelect) {
							newForm.subSelect.push({
								subSelectQryName: subSelect.subSelectQryName,
								subSelectChildName: subSelect.subSelectChildName,
								parameters: subSelect.field_as_parameters,
							});
						}

					const newFormItemArray: genericFormInterface[] = [];
					for (const item of formItem.items) {
						const newFormItem: genericFormInterface = {
							values: [item.value],
							name: item.name,
							label: item.label,
						};

						newFormItemArray.push(newFormItem);
					}

					newForm.items = newFormItemArray;

					newFormArray.push(newForm);
				});

				ciFormRes.customFields.forEach((formItem: any) => {
					const newForm: genericFormInterface = {
						id: formItem.id,
						disabled: false,
						items: [],
						label: formItem.label,
						name: formItem.name,
						required: false,
						hidden: formItem.hidden,
						values: formItem.values !== null && formItem.values.length > 0 ? formItem.values : [''],
						inputType: formItem.inputType,
						subSelect: [],
					};

					if (formItem.subSelect)
						for (const subSelect of formItem.subSelect) {
							newForm.subSelect.push({
								subSelectQryName: subSelect.subSelectQryName,
								subSelectChildName: subSelect.subSelectChildName,
								parameters: subSelect.field_as_parameters,
							});
						}

					const newFormItemArray: genericFormInterface[] = [];
					for (const item of formItem.items) {
						const newFormItem: genericFormInterface = {
							values: [item.value],
							name: item.name,
							label: item.label,
							value: item.value,
						};

						newFormItemArray.push(newFormItem);
					}

					newForm.items = newFormItemArray;

					newFormArray.push(newForm);
				});

				return newFormArray;
			})
		);
	}

	getCiLogs(idCi: string): Observable<logInterface[]> {
		const getCiLogsUrl = this.getCiLogsUrl.replace('{idCi}', idCi);

		return this.LabelsService.getLabels().pipe(
			switchMap((labels) => {
				return this.http.get<JSON[]>(getCiLogsUrl, { headers: this.headers }).pipe(
					map((logRes) => {
						const newLogInterfaceArray: logInterface[] = [];
						logRes.forEach((log: any) => {
							const newLog: logInterface = {
								status: log.action,
								duration: '',
								file_log: [],
								analyst: log.email,
								idLog: '',
								log_date: log.created_at,
								notes: log.message.replaceAll(/§+/g, ', '),
							};

							newLogInterfaceArray.push(newLog);
						});

						return newLogInterfaceArray;
					})
				);
			})
		);
	}

	getCiMinData(idCi: string[]): Observable<ciInterface2[]> {
		const getCiMinDataUrl = this.getCiMinDataUrl.replace('{idCi}', idCi.join(','));
		return this.http.get<ciInterface2[]>(getCiMinDataUrl, { headers: this.headers }).pipe(
			map((ci) => {
				return ci;
			})
		);
	}

	getCiBaselineData(idCi: string, formatedDate: string): Observable<ciInterface> {
		const getCiBaselineUrl = this.getCiBaselineUrl.replace('{idCi}', idCi).replace('{queryString}', `date=${formatedDate}`);

		return this.http.get<JSON>(getCiBaselineUrl, { headers: this.headers }).pipe(
			map((ciRes: any) => {
				const newCi: ciInterface = {
					ciType: ciRes.cmdb.type,
					ciArray: [
						{
							id: ciRes.cmdb.id,
							cmdb_id: ciRes.cmdb.id,
							type: ciRes.cmdb.type,
							identification: ciRes.cmdb.identification,
							last_audit: ciRes.cmdb.last_audit,
							active: ciRes.cmdb.active,
							location: ciRes.cmdb.location,
							notes: ciRes.cmdb.notes,
							status: ciRes.cmdb.status,
							substatus: ciRes.cmdb.substatus,
							monthly_depreciation: ciRes.cmdb.monthly_depreciation,
							ready_to_audit: ciRes.cmdb.ready_to_audit,
							cc_customer: ciRes.cmdb.cc_customer,
							cost_center: ciRes.cmdb.cost_center,
							idCustomer: ciRes.cmdb.customer,
							customer: ciRes.cmdb.customer,
							project: ciRes.cmdb.project,
							price: ciRes.cmdb.price,
							idCiType: ciRes.cmdb.type,
							invoice_number: ciRes.cmdb.invoice_number,
							enduser: ciRes.cmdb.enduser,
							warranty_type: ciRes.cmdb.warranty_type,
							impact: ciRes.cmdb.impact,
							idImpact: ciRes.cmdb.impact,
							next_audit: ciRes.cmdb.next_audit,
							department: ciRes.cmdb.department,
							purchase_order: ciRes.cmdb.purchase_order,
							depreciation_limit: ciRes.cmdb.depreciation_limit,
							physical_location: ciRes.cmdb.physical_location,
							purchase_date: ciRes.cmdb.purchase_date,
							warranty_limit: ciRes.cmdb.warranty_limit,
							deactivation_date: ciRes.cmdb.deactivation_date,
							tag_id: ciRes.cmdb.tag_id,
						},
					],
				};

				if (ciRes.customFields) {
					ciRes.customFields.forEach((ciCustomItem: any) => {
						if (!newCi.ciArray[0][ciCustomItem] || newCi.ciArray[0][ciCustomItem].toString().trim() == '') {
							newCi.ciArray[0][ciCustomItem.field] = ciCustomItem.content;
						} else {
							console.warn(`The field ${ciCustomItem} has an dupped name and the app don't know how to handle it.`);
						}
					});
				}
				if (ciRes.ci) {
					for (const ciCustomItem of Object.keys(ciRes.ci)) {
						if (!newCi.ciArray[0][ciCustomItem] || newCi.ciArray[0][ciCustomItem].toString().trim() == '') {
							newCi.ciArray[0][ciCustomItem] = ciRes.ci[ciCustomItem];
						} else {
							console.warn(`The field ${ciCustomItem} has an dupped name and the app don't know how to handle it.`);
						}
					}
				}
				if (ciRes.cmdb) {
					for (const cmdbItem of Object.keys(ciRes.cmdb)) {
						if (!newCi.ciArray[0][cmdbItem] || newCi.ciArray[0][cmdbItem].toString().trim() == '') {
							newCi.ciArray[0][cmdbItem] = ciRes.cmdb[cmdbItem];
						} else {
							console.warn(`The field ${cmdbItem} has an dupped name and the app don't know how to handle it.`);
						}
					}
				}
				return newCi;
			})
		);
	}

	getFileList(idCi: string): Observable<mspFileInterface[]> {
		const getFileListUrl = this.getFileListUrl.replace('{idCi}', idCi);
		return this.http.get<any[]>(getFileListUrl, { headers: this.headers }).pipe(
			map((fileListRes) => {
				const newMspFileList: mspFileInterface[] = [];
				for (const fileRes of fileListRes) {
					const newMspFile: mspFileInterface = {
						file_path: fileRes.fileAttach,
						id: fileRes.id,
						checkSum: fileRes.checksum,
						content: '',
						extension: '',
						mimeType: '',
						size: 0,
						name: fileRes.fileName,
					};

					newMspFileList.push(newMspFile);
				}

				return newMspFileList;
			})
		);
	}

	getFile(idCi: string, idFile: string): Observable<mspFileInterface> {
		const getFileUrl = this.getFileUrl.replace('{idCi}', idCi).replace('{idFile}', idFile);
		return this.http.get<mspFileInterface>(getFileUrl, { headers: this.headers });
	}

	getCiStatus(idCi: string): Observable<logInterface[]> {
		const getCiStatusLogUrl = this.getCiStatusLog.replace('{idCi}', idCi);

		return this.LabelsService.getLabels().pipe(
			switchMap((labels) => {
				return this.http.get<JSON[]>(getCiStatusLogUrl, { headers: this.headers }).pipe(
					map((logRes) => {
						const newLogInterfaceArray: logInterface[] = [];
						logRes.forEach((log: any) => {
							let logMessage = '';

							logMessage += `${labels.lbl_geral_de} "${log.oldValue}" ${labels.lbl_geral_para} "${log.newValue}".`;

							const newLog: logInterface = {
								status: '',
								duration: '',
								analyst: log.email,
								idLog: log.id,
								log_date: log.created_at,
								notes: logMessage,
							};

							newLogInterfaceArray.push(newLog);
						});

						return newLogInterfaceArray;
					})
				);
			})
		);
	}

	getCiEditPermission(ciType): Observable<any> {
		const getCMDBViewPermissions = this.getCmdbPermissionsUrl.replace('{idCiType}', ciType);
		return this.http.get<JSON>(getCMDBViewPermissions, { headers: this.headers }).pipe(
			map((permission) => {
				return permission[0];
			})
		);
	}

	formatDateToService(date: Date): string {
		date = new Date(date);
		return `${date.getFullYear()}-${date.getMonth() + 1 < 10 ? '0' + (date.getMonth() + 1) : date.getMonth() + 1}-${date.getDate() < 10 ? '0' + date.getDate() : date.getDate()}`;
	}

	getExistingWorkstationsInAd(idCi: string) {
		const getExistingWorkstationOnAd = this.getExistingWorkstationOnAd + `?cmdb_id=${idCi}`;
		return this.http.get<JSON[]>(getExistingWorkstationOnAd, { headers: this.headers });
	}

	getCmdbDigitalTerm(idCMDB: string): Observable<CmdbDigitalTerm[]> {
		const getCmdbDigitalTermUrl = this.getLogDigitalTermUrl.replace('{idCMDB}', idCMDB);
		return this.http.get<CmdbDigitalTerm[]>(getCmdbDigitalTermUrl, { headers: this.headers });
	}

	getTermFile(fileName: string): Observable<any> {
		const getTermFileUrl = this.getTermFileUrl.replace('{fileName}', fileName);
		return this.http.get<any>(getTermFileUrl, { headers: this.headers });
	}
}
